import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DatabaseService } from '../../core/services/database.service';
import { WebsitesService } from '../../core/services/websites.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import {
  switchMap,
  map,
  shareReplay,
  of,
  forkJoin,
  filter,
  Observable,
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
} from 'rxjs';
import {
  Database,
  DatabaseStatus,
  DatabaseRecord,
  DatabaseUser,
  DatabaseUserRecord,
  DatabaseUserStatus,
  DatabaseCreate,
  DatabaseUserCreate,
} from '../../shared/models/database.models';
import { AsyncPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { TableComponent } from '../../shared/components/table/table.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SearchFieldComponent } from '../../shared/components/search-field/search-field.component';
import { ServersService } from '../../core/services/servers.service';
import { Status } from '../../shared/models/status-label.model';
import { MatTableModule } from '@angular/material/table';
import { StatusLabelComponent } from '../../shared/components/status-label/status-label.component';
import { ConfirmationDialogComponent } from '../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from '../../shared/components/snackbar/snackbar.component';
import { StatusModalService } from '../../core/services/status-modal.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { WebsiteSearchResponse } from '../../shared/models/website.models';
import { AddDatabaseDialogComponent } from './dialogs/add-database-dialog/add-database-dialog.component';
import { AddDatabaseUserDialogComponent } from '../website-overview/dialogs/add-database-user-dialog/add-database-user-dialog.component';
import { AddDatabaseUsersDialogComponent } from './dialogs/add-database-users-dialog/add-database-users-dialog.component';
import { ChangePasswordDialogComponent } from '../website-overview/dialogs/change-password-dialog/change-password-dialog.component';
import { WebsocketService } from '../../core/services/websocket.service';
import { MatRippleModule } from '@angular/material/core';

@Component({
  selector: 'app-databases',
  imports: [
    AsyncPipe,
    MatTabsModule,
    MatCardModule,
    MatTableModule,
    MatRippleModule,
    TableComponent,
    StatusLabelComponent,
    SearchFieldComponent,
    FontAwesomeModule,
    MatTooltipModule,
  ],
  templateUrl: './databases.component.html',
  styleUrl: './databases.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatabasesComponent implements OnInit {
  private databaseService = inject(DatabaseService);

  private websitesService = inject(WebsitesService);

  private serversService = inject(ServersService);

  private statusModalService = inject(StatusModalService);

  private websocketService = inject(WebsocketService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  databasePage = signal(1);

  databaseUserPage = signal(1);

  databaseSearch = signal('');

  databaseUserSearch = signal('');

  private databasePage$ = toObservable(this.databasePage);

  private databaseUserPage$ = toObservable(this.databaseUserPage);

  private refresh$ = new BehaviorSubject<void>(undefined);

  private readonly destroyRef = inject(DestroyRef);

  private databaseSearch$ = toObservable(this.databaseSearch).pipe(
    debounceTime(500),
    distinctUntilChanged(),
  );

  private databaseUserSearch$ = toObservable(this.databaseUserSearch).pipe(
    debounceTime(500),
    distinctUntilChanged(),
  );

  private getAllWebsitesByIds(ids: number[]): Observable<WebsiteSearchResponse> {
    if (!ids.length) {
      return of({ Websites: [], count: 0 });
    }

    const batchSize = 10;
    const uniqueIds = [...new Set(ids)];

    const requests: Observable<WebsiteSearchResponse>[] = [];

    for (let i = 0; i < uniqueIds.length; i += batchSize) {
      const batch = uniqueIds.slice(i, i + batchSize);

      requests.push(this.websitesService.getWebsitesByIds(batch));
    }

    return forkJoin(requests).pipe(
      map((responses) => {
        const websites = responses.flatMap((response) => response.Websites);

        return {
          Websites: websites,
          count: websites.length,
        };
      }),
    );
  }

  databases$ = combineLatest([this.databasePage$, this.databaseSearch$, this.refresh$]).pipe(
    switchMap(([page, search]) =>
      this.databaseService.getDatabases(page, search.trim() || undefined),
    ),
    switchMap(([databases, count]) => {
      if (!databases.length) {
        return of({ databases: [] as DatabaseRecord[], count });
      }

      const websiteIds = [...new Set(databases.map((db) => db.websiteId))];

      const serverIds = [...new Set(databases.map((db) => db.server))];

      return forkJoin({
        websites: this.getAllWebsitesByIds(websiteIds),
        servers: this.serversService.getServersByIds(serverIds),
      }).pipe(
        map(({ websites, servers }) => {
          const websiteMap = new Map(
            websites.Websites.map((website) => [website.id, website.domain]),
          );

          const serverMap = new Map(servers.map((server) => [server._id, server.hostname]));

          const records: DatabaseRecord[] = databases.map((database) => ({
            ...database,
            websiteDomain: websiteMap.get(database.websiteId) ?? '-',
            serverHostname: serverMap.get(database.server) ?? '-',
          }));

          return { databases: records, count };
        }),
      );
    }),
    shareReplay(1),
  );

  databaseUsers$ = combineLatest([
    this.databaseUserPage$,
    this.databaseUserSearch$,
    this.refresh$,
  ]).pipe(
    switchMap(([page, search]) => this.databaseService.getUsers(page, search.trim() || undefined)),
    switchMap(([users, count]) => {
      if (!users.length) {
        return of({ users: [] as DatabaseUserRecord[], count });
      }

      const databases = users.flatMap((user) => user.databases);

      const websiteIds = [...new Set(databases.map((db) => db.websiteId))];

      const serverIds = [
        ...new Set(
          users.map((user) => user.databases[0]?.server).filter((id): id is string => !!id),
        ),
      ];

      return forkJoin({
        websites: this.getAllWebsitesByIds(websiteIds),
        servers: serverIds.length ? this.serversService.getServersByIds(serverIds) : of([]),
      }).pipe(
        map(({ websites, servers }) => {
          const websiteMap = new Map(
            websites.Websites.map((website) => [website.id, website.domain]),
          );

          const serverMap = new Map(servers.map((server) => [server._id, server.hostname]));

          const records: DatabaseUserRecord[] = users.map((user) => ({
            ...user,
            websiteDomains: user.databases.map((db) => websiteMap.get(db.websiteId) ?? '-'),
            serverHostname: serverMap.get(user.databases[0]?.server) ?? '-',
          }));

          return { users: records, count };
        }),
      );
    }),
    shareReplay(1),
  );

  databasesColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: Database) => `${element.id}`,
    },
    {
      columnDef: 'name',
      header: 'Database Name',
      cell: (element: Database) => `${element.name}`,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: DatabaseRecord) => element.websiteDomain ?? '-',
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: Database) => DatabaseStatus[element.status] ?? '-',
      useTemplate: true,
    },
    {
      columnDef: 'server',
      header: 'Server',
      cell: (element: DatabaseRecord) => element.serverHostname ?? '-',
    },
    {
      columnDef: 'size',
      header: 'Size',
      cell: (element: Database) => `${element.size}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  databaseUsersDetailsColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: DatabaseUser) => `${element.id}`,
    },
    {
      columnDef: 'type',
      header: 'Type',
      cell: (element: DatabaseUser) => (element.unixUser ? 'Main' : 'DB User'),
    },
    {
      columnDef: 'username',
      header: 'Database User',
      cell: (element: DatabaseUser) => element.username,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: DatabaseUser) => DatabaseUserStatus[element.status] ?? '-',
      useTemplate: true,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  databasesUsersColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: DatabaseUser) => `${element.id}`,
    },
    {
      columnDef: 'username',
      header: 'Database User',
      cell: (element: DatabaseUser) => `${element.username}`,
    },
    {
      columnDef: 'databases',
      header: 'Databases',
      cell: (element: DatabaseUserRecord) =>
        element.databases
          .slice(0, 3)
          .map((db) => db.name)
          .join(', ') || '-',
      useTemplate: true,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: DatabaseUserRecord) => element.websiteDomains.slice(0, 3).join(', ') || '-',
      useTemplate: true,
    },
    {
      columnDef: 'server',
      header: 'Server',
      cell: (element: DatabaseUserRecord) => element.serverHostname,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  userDatabasesColumns = [
    {
      columnDef: 'id',
      header: 'ID',
      cell: (element: DatabaseRecord) => `${element.id}`,
    },
    {
      columnDef: 'name',
      header: 'Database Name',
      cell: (element: DatabaseRecord) => `${element.name}`,
    },
    {
      columnDef: 'website',
      header: 'Website',
      cell: (element: DatabaseRecord) => `${element.websiteDomain}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  ngOnInit(): void {
    this.websocketService
      .listen<{ title: string; body: string }>('notification')
      .pipe(
        filter(
          ({ title, body }) =>
            /^Database for website with id \d+ created successfully\.$/.test(body) ||
            title === 'Database Deleted!' ||
            title === 'Database User Created' ||
            title === 'Database User Deleted',
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.refresh$.next();
      });
  }

  toStatus(key: DatabaseStatus): Status {
    switch (key) {
      case DatabaseStatus.IN_PROGRESS:
        return Status.IN_PROGRESS;
      case DatabaseStatus.DONE:
        return Status.ACTIVE;
      case DatabaseStatus.ERROR:
        return Status.ERROR;
      case DatabaseStatus.ACTIVE:
        return Status.ACTIVE;
      case DatabaseStatus.DISABLED:
        return Status.DISABLED;
      default:
        return Status.ERROR;
    }
  }

  toUserStatus(key: DatabaseUserStatus): Status {
    switch (key) {
      case DatabaseUserStatus.CREATING:
        return Status.IN_PROGRESS;
      case DatabaseUserStatus.CREATED:
        return Status.ACTIVE;
      case DatabaseUserStatus.ERROR:
        return Status.ERROR;
      default:
        return Status.ERROR;
    }
  }

  onDatabaseSearch(search: string): void {
    this.databasePage.set(1);
    this.databaseSearch.set(search.trim());
  }

  onDatabaseUserSearch(search: string): void {
    this.databaseUserPage.set(1);
    this.databaseUserSearch.set(search.trim());
  }

  handleDatabasePageEvent(event: PageEvent): void {
    this.databasePage.set(event.pageIndex + 1);
  }

  handleDatabaseUserPageEvent(event: PageEvent): void {
    this.databaseUserPage.set(event.pageIndex + 1);
  }

  getDatabaseNames(user: DatabaseUserRecord): string {
    return (
      user.databases
        .slice(0, 3)
        .map((database) => database.name)
        .join(', ') || '-'
    );
  }

  getWebsiteDomains(user: DatabaseUserRecord): string {
    const domains = user.websiteDomains.slice(0, 3).join(', ');

    return user.websiteDomains.length > 3 ? `${domains} ...` : domains || '-';
  }

  getExpandedDatabases(user: DatabaseUserRecord): DatabaseRecord[] {
    return user.databases.map((database, index) => ({
      ...database,
      websiteDomain: user.websiteDomains[index] ?? '-',
      serverHostname: user.serverHostname,
    }));
  }

  deleteDatabase(database: DatabaseRecord): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete Database',
        paragraphs: [
          `This action will permanently delete the database ${database.name}.`,
          `If this database is used by the site, deleting it will cause downtime.`,
          `Are you sure you want to delete the database ${database.name}?`,
        ],
        primaryButtonText: 'Delete Database',
        type: 'delete',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.databaseService.deleteDatabase(database.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Deletion of Database ${database.name} started.`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || 'Failed to delete Database.',
          });
        },
      });
  }

  deleteDatabaseUser(user: DatabaseUser): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete Database User',
        paragraphs: [
          `This action will permanently delete database user ${user.username} and revoke its access to all assigned databases.`,
          `If this database user is used in your website's database configuration, the website may stop working until the database credentials are updated.`,
          `Are you sure you want to delete database user ${user.username}?`,
        ],
        primaryButtonText: 'Delete Database User',
        type: 'delete',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.databaseService.deleteUser(user.id)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `Deletion of Database User ${user.username} started.`,
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || 'Failed to delete Database User.',
          });
        },
      });
  }

  addDatabase(): void {
    this.dialog
      .open<AddDatabaseDialogComponent, void, DatabaseCreate>(AddDatabaseDialogComponent)
      .afterClosed()
      .pipe(
        filter((data): data is DatabaseCreate => !!data),
        switchMap((data) => this.databaseService.createDatabase(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'Database creation started!',
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || error.message || 'Failed to create database.',
          });
        },
      });
  }

  addDatabaseUser(database: DatabaseRecord): void {
    this.dialog
      .open(AddDatabaseUserDialogComponent, {
        data: {
          subTitle: database.websiteDomain ?? '',
          databaseId: database.id,
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data: DatabaseUserCreate) => this.databaseService.createUser(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'Database user creation started!',
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || error.message,
          });
        },
      });
  }

  addDatabaseUserWithSelection(): void {
    this.dialog
      .open<AddDatabaseUsersDialogComponent, void, DatabaseUserCreate>(
        AddDatabaseUsersDialogComponent,
      )
      .afterClosed()
      .pipe(
        filter((data): data is DatabaseUserCreate => !!data),
        switchMap((data) => this.databaseService.createUser(data)),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'Database user creation started!',
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || error.message || 'Failed to create database user.',
          });
        },
      });
  }

  editDatabaseUser(user: DatabaseUser, databaseId?: number): void {
    const selectedDatabaseId = databaseId ?? user.databases[0]?.id;

    if (!selectedDatabaseId) {
      this.statusModalService.open({
        state: 'error',
        message: 'No database is associated with this user.',
      });
      return;
    }

    this.dialog
      .open(ChangePasswordDialogComponent, {
        data: { subTitle: user.username },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap((data) =>
          this.databaseService.editUser(user.id, {
            ...data,
            databaseId: selectedDatabaseId,
          }),
        ),
      )
      .subscribe({
        next: () => {
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: 'Database user password change started!',
            },
          });
        },
        error: (error) => {
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error || error.message || 'Failed to update database user.',
          });
        },
      });
  }
}
